import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/LoadingSpinner';
import { CampaignList } from './components/CampaignList';
import { Calendar } from './components/Calendar';
import { CampaignForm } from './components/CampaignForm';
import { getCampaigns, createCampaign, updateCampaign as updateCampaignAPI, deleteCampaign, checkForOverlap } from './services/campaignService';
import Dashboard from './components/Dashboard';
import EmailAnalyzerPage from './pages/EmailAnalyzerPage';
import EmailAnalyzerTestPage from './pages/EmailAnalyzerTestPage';
import RCFCampaignPlannerPage from './features/rcf-planner/RCFCampaignPlannerPage';
import { Campaign } from './types/Campaign';
// Import FormPreviewPage for testing
import FormPreviewPage from './features/ai-sequence/components/FormPreviewPage';
import { shouldShowDebugFeatures } from './utils/environmentUtils';

// Lazy load authentication pages
const LoginPage = lazy(() => import('./components/LoginPage'));
const VerifyPage = lazy(() => import('./pages/VerifyPage'));
const LinkErrorPage = lazy(() => import('./pages/LinkErrorPage'));
const AuthDebugPage = lazy(() => import('./pages/AuthDebugPage'));
const AiSequencePage = lazy(() => import('./features/ai-sequence/AiSequencePage'));
const UserAiSequencePage = lazy(() => import('./features/ai-sequence/components/user/UserAiSequencePage'));
const AdminDashboard = lazy(() => import('./features/ai-sequence/components/admin/AdminDashboard'));
const SimpleFormDesigner = lazy(() => import('./features/ai-sequence/components/admin/SimpleFormDesigner'));
const PromptManager = lazy(() => import('./features/ai-sequence/components/admin/PromptManager'));
const DocumentManager = lazy(() => import('./features/ai-sequence/components/admin/DocumentManager'));

const WorkflowBuilderOverview = lazy(() => import('./features/ai-sequence/components/admin/WorkflowBuilderOverview'));
const WorkflowStepBuilder = lazy(() => import('./features/ai-sequence/components/admin/WorkflowStepBuilder'));
const WorkflowsLandingPage = lazy(() => import('./features/ai-sequence/components/admin/WorkflowsLandingPage'));
const InstructionSetsPage = lazy(() => import('./features/ai-sequence/components/admin/instruction-sets/InstructionSetsPage'));
const ApprovalWorkflowDemo = lazy(() => import('./features/ai-sequence/components/approval/ApprovalWorkflowDemo'));
const WorkflowRunner = lazy(() => import('./features/ai-sequence/components/user/WorkflowRunner'));
const WorkflowChatEditor = lazy(() => import('./features/ai-sequence/components/user/WorkflowChatEditor'));

// ProtectedRoute component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();

  if (isInitializing) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Root redirect component
const RootRedirect: React.FC = () => {
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Handle token in URL for magic link
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    
    if (token && location.pathname === '/') {
      navigate(`/verify?token=${token}`, { replace: true });
      return;
    }
    
    // Redirect based on auth status
    if (!isInitializing && location.pathname === '/') {
      const destination = isAuthenticated ? '/dashboard' : '/login';
      navigate(destination, { replace: true });
    }
  }, [isInitializing, isAuthenticated, location, navigate]);
  
  if (isInitializing) {
    return <LoadingSpinner />;
  }
  
  return null;
};

// Inner App component that uses Auth context
const AppContent = () => {
  const { isAuthenticated, user, logout, isInitializing, updateUserPreferences } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get timezone from user profile (or default)
  const currentSelectedTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Load campaigns when authenticated
  useEffect(() => {
    if (isAuthenticated && currentSelectedTimezone) {
      loadCampaigns();
    }
  }, [isAuthenticated, currentSelectedTimezone]);
  
  // Redirect to dashboard if authenticated at root or if coming from verification
  useEffect(() => {
    if (isAuthenticated) {
      if (location.pathname === '/' || 
          location.pathname === '/verify' || 
          location.pathname === '/link-error') {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, location.pathname, navigate]);
  
  const loadCampaigns = async () => {
    if (!currentSelectedTimezone) {
      return;
    }
    
    setIsLoadingCampaigns(true);
    setError(null);
    
    try {
      const data = await getCampaigns(currentSelectedTimezone);
      setCampaigns(data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      setError('Failed to load campaigns. Please try refreshing the page.');
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  // Show loading spinner during auth initialization
  if (isInitializing) {
    return <LoadingSpinner />;
  }

  // Campaign handlers
  const handleEditCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowForm(true);
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    try {
      // Optimistically update UI
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
      
      // Delete from server
      await deleteCampaign(campaignId);
      
      // Reset UI state
      setSelectedCampaign(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      // Reload campaigns to restore state
      loadCampaigns();
      setError('Failed to delete campaign. Please try again.');
    }
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setShowForm(true);
  };

  const handleCampaignSubmit = async (campaignData: Omit<Campaign, 'id'>) => {
    try {
      setError(null);
      
      if (selectedCampaign) {
        // Update existing campaign
        const updated = await updateCampaignAPI(
          selectedCampaign.id,
          campaignData,
          campaigns,
          currentSelectedTimezone
        );
        
        setCampaigns(campaigns.map(c => c.id === updated.id ? updated : c));
      } else {
        // Create new campaign
        const newCampaign = await createCampaign(
          campaignData,
          campaigns,
          currentSelectedTimezone
        );
        
        setCampaigns([...campaigns, newCampaign]);
      }
      
      // Reset form state
      setShowForm(false);
      setSelectedCampaign(null);
      setSelectedDate(null);
    } catch (error: any) {
      console.error('Error saving campaign:', error);
      setError(error.message || 'Failed to save campaign. Please try again.');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedCampaign(null);
    setSelectedDate(null);
  };

  const handleTimezoneUpdate = async (timezone: string) => {
    try {
      await updateUserPreferences({ timezone });
      // Reload campaigns with new timezone
      loadCampaigns();
    } catch (error) {
      console.error('Failed to update timezone:', error);
      setError('Failed to update timezone. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Only show when authenticated */}
      {isAuthenticated && user && (
        <header className="bg-[#1E3B8C] text-white shadow relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <h1 className="text-xl font-bold">Sequence Planner</h1>
              </div>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-2">
                <Link
                  to="/dashboard"
                  className={`${location.pathname === '/dashboard' ? 'bg-white/20' : 'bg-white/10'} hover:bg-white/20 text-white hover:text-white px-3 py-1.5 rounded-md text-sm transition-colors`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/rcf-campaign-planner"
                  className={`${location.pathname === '/rcf-campaign-planner' ? 'bg-white/20' : 'bg-white/10'} hover:bg-white/20 text-white hover:text-white px-3 py-1.5 rounded-md text-sm transition-colors`}
                >
                  Campaign Planner
                </Link>
                <Link
                  to="/email-analyzer"
                  className={`${location.pathname === '/email-analyzer' ? 'bg-white/20' : 'bg-white/10'} hover:bg-white/20 text-white hover:text-white px-3 py-1.5 rounded-md text-sm transition-colors`}
                >
                  Email Analyzer <span className="bg-orange-500 text-white px-1.5 py-0.5 rounded text-xs ml-1 font-medium">alpha</span>
                </Link>
                <Link
                  to="/ai-sequence"
                  className={`${location.pathname.startsWith('/ai-sequence') ? 'bg-white/20' : 'bg-white/10'} hover:bg-white/20 text-white hover:text-white px-3 py-1.5 rounded-md text-sm transition-colors`}
                >
                  AI Sequence <span className="bg-blue-500 text-white px-1.5 py-0.5 rounded text-xs ml-1 font-medium">beta</span>
                </Link>
                <a
                  href="https://mechanicalmarketing.freshdesk.com/support/solutions/11000006016"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 text-white hover:text-white px-3 py-1.5 rounded-md text-sm transition-colors"
                >
                  Tutorials & Help
                </a>
                <div className="flex items-center">
                  <span className="text-sm mr-4">{user?.email || 'User'}</span>
                  <button
                    onClick={handleLogout}
                    className="bg-white/10 hover:bg-white/20 text-white hover:text-white px-3 py-1.5 rounded-md text-sm transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>

              {/* Mobile menu button */}
              <div className="md:hidden flex items-center">
                <span className="text-sm mr-3">{user?.email || 'User'}</span>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 bg-white hover:bg-gray-100 rounded-md transition-colors"
                  aria-label="Toggle menu"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="#133d88"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden absolute top-16 left-0 right-0 bg-[#1E3B8C] border-t border-white/10 shadow-lg z-50">
              <div className="px-4 py-2 space-y-1">
                <Link
                  to="/dashboard"
                  className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                    location.pathname === '/dashboard' 
                      ? 'bg-white/20 text-white' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/rcf-campaign-planner"
                  className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                    location.pathname === '/rcf-campaign-planner' 
                      ? 'bg-white/20 text-white' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Campaign Planner
                </Link>
                <Link
                  to="/email-analyzer"
                  className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                    location.pathname === '/email-analyzer' 
                      ? 'bg-white/20 text-white' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Email Analyzer</span>
                  <span className="bg-orange-500 text-white px-1.5 py-0.5 rounded text-xs ml-2 font-medium">alpha</span>
                </Link>
                <Link
                  to="/ai-sequence"
                  className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                    location.pathname.startsWith('/ai-sequence') 
                      ? 'bg-white/20 text-white' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>AI Sequence</span>
                  <span className="bg-blue-500 text-white px-1.5 py-0.5 rounded text-xs ml-2 font-medium">beta</span>
                </Link>
                <a
                  href="https://mechanicalmarketing.freshdesk.com/support/solutions/11000006016"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-3 py-2 rounded-md text-sm text-white hover:bg-white/10 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Tutorials & Help
                </a>
                <div className="border-t border-white/10 mt-2 pt-2">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-sm text-white hover:bg-white/10 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>
      )}

      {/* Main Content */}
      <main className={isAuthenticated ? "max-w-7xl mx-auto py-6 sm:px-6 lg:px-8" : ""}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={
            <Suspense fallback={<LoadingSpinner />}>
              <LoginPage />
            </Suspense>
          } />
          <Route path="/verify" element={
            <Suspense fallback={<LoadingSpinner />}>
              <VerifyPage />
            </Suspense>
          } />
          <Route path="/link-error" element={
            <Suspense fallback={<LoadingSpinner />}>
              <LinkErrorPage />
            </Suspense>
          } />
          {/* Debug route - only available in development/staging */}
          {shouldShowDebugFeatures() && (
            <Route path="/auth-debug" element={
              <Suspense fallback={<LoadingSpinner />}>
                <AuthDebugPage />
              </Suspense>
            } />
          )}
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard
                campaigns={campaigns}
                isLoadingCampaigns={isLoadingCampaigns}
                error={error}
                onEditCampaign={handleEditCampaign}
                onDeleteCampaign={handleDeleteCampaign}
                onDateSelect={handleDateSelect}
                onCampaignSubmit={handleCampaignSubmit}
                onFormClose={handleFormClose}
                showForm={showForm}
                selectedCampaign={selectedCampaign}
                selectedDate={selectedDate}
                currentSelectedTimezone={currentSelectedTimezone}
                onTimezoneUpdate={handleTimezoneUpdate}
                onLogout={handleLogout}
              />
            </ProtectedRoute>
          } />
          
          <Route path="/email-analyzer" element={
            <ProtectedRoute>
              <EmailAnalyzerPage />
            </ProtectedRoute>
          } />
          
          {/* Test route - only available in development/staging */}
          {shouldShowDebugFeatures() && (
            <Route path="/email-analyzer-test" element={
              <ProtectedRoute>
                <EmailAnalyzerTestPage />
              </ProtectedRoute>
            } />
          )}
          
          <Route path="/rcf-campaign-planner" element={
            <ProtectedRoute>
              <RCFCampaignPlannerPage />
            </ProtectedRoute>
          } />
          
          {/* AI Sequence Routes */}
          <Route path="/ai-sequence" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <UserAiSequencePage />
              </Suspense>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-sequence/user" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <UserAiSequencePage />
              </Suspense>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-sequence/admin" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <AdminDashboard />
              </Suspense>
            </ProtectedRoute>
          }>
            <Route index element={
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowBuilderOverview />
              </Suspense>
            } />
            <Route path="resources" element={<Navigate to="/ai-sequence/admin/forms" replace />} />
            <Route path="forms" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SimpleFormDesigner />
              </Suspense>
            } />
            <Route path="prompts" element={
              <Suspense fallback={<LoadingSpinner />}>
                <PromptManager />
              </Suspense>
            } />
            <Route path="documents" element={
              <Suspense fallback={<LoadingSpinner />}>
                <DocumentManager />
              </Suspense>
            } />
            <Route path="workflows" element={
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowsLandingPage />
              </Suspense>
            } />
            <Route path="workflows/builder" element={
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowBuilderOverview />
              </Suspense>
            } />
            <Route path="workflows/steps/:workflowId" element={
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowStepBuilder />
              </Suspense>
            } />
            <Route path="workflows/:workflowId" element={
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowStepBuilder />
              </Suspense>
            } />
            <Route path="instruction-sets" element={
              <Suspense fallback={<LoadingSpinner />}>
                <InstructionSetsPage />
              </Suspense>
            } />
          </Route>
          
          <Route path="/ai-sequence/approval-demo" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ApprovalWorkflowDemo />
              </Suspense>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-sequence/workflow/:workflowId" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowRunner />
              </Suspense>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-sequence/chat-editor/:executionId" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowChatEditor />
              </Suspense>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-sequence/form-preview/:formId" element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <FormPreviewPage />
              </Suspense>
            </ProtectedRoute>
          } />
          
          {/* Catch all - redirect to dashboard if authenticated, login if not */}
          <Route path="*" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />
        </Routes>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
